import { ContentTypesEnum } from '../../types/contentTypesEnum';
import { debounce } from '../../utils/debounce';
import { SyncStatus } from '../../utils/syncStatus';
import { UpdateStatus } from '../../utils/updateStatus';

const CM_BASE = '/content-manager/collection-types';
const DATA_SOURCE_PATH = `${CM_BASE}/${ContentTypesEnum.DATA_SOURCE}`;
const STATISTIC_PATH = `${CM_BASE}/${ContentTypesEnum.STATS}`;

interface BadgeStyle {
  bg: string;
  color: string;
  border: string;
}

/**
 * `updateStatus` (fraîcheur éditoriale de la donnée, tenue par le cron) et
 * `syncStatus` (propagation technique du chart vers data-roole-api) sont deux
 * axes distincts, rendus par le même mécanisme : leurs valeurs ne se recoupent
 * pas, donc un simple match sur le texte du span suffit à les distinguer.
 */
const STATUS_STYLES: Record<UpdateStatus | SyncStatus, BadgeStyle> = {
  [UpdateStatus.UpToDate]: { bg: '#d4edda', color: '#155724', border: '#c3e6cb' },
  [UpdateStatus.NeedsUpdate]: { bg: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
  [UpdateStatus.NeedsUpdateStats]: { bg: '#fff3cd', color: '#856404', border: '#ffeeba' },
  [UpdateStatus.NeedsUpdateDataSource]: { bg: '#f8d7da', color: '#491217', border: '#f1aeb5' },
  [SyncStatus.Synced]: { bg: '#d4edda', color: '#155724', border: '#c3e6cb' },
  [SyncStatus.Failed]: { bg: '#f5c2c7', color: '#58151c', border: '#dc3545' },
};
const STATUS_LABELS: Record<UpdateStatus | SyncStatus, string> = {
  [UpdateStatus.UpToDate]: 'A jour',
  [UpdateStatus.NeedsUpdate]: 'A mettre à jour',
  [UpdateStatus.NeedsUpdateStats]: 'A mettre à jour (stats)',
  [UpdateStatus.NeedsUpdateDataSource]: 'A mettre à jour (data source)',
  [SyncStatus.Synced]: 'Propagée',
  [SyncStatus.Failed]: 'Echec de propagation',
};
const STATUS_VALUES = Object.keys(STATUS_STYLES) as (UpdateStatus | SyncStatus)[];
const INJECTED_STYLE_ID = 'strapi-status-labels-style';
const DEBOUNCE_DELAY = 300;
const NAVIGATION_CHECK_INTERVAL = 500;
const INITIAL_STYLE_DELAY = 1_000;

const isTargetPage = (): boolean => {
  const path = globalThis.location.pathname;

  return path.includes(DATA_SOURCE_PATH) || path.includes(STATISTIC_PATH);
};

const isLeafSpan = (span: HTMLSpanElement): boolean => {
  return span.querySelectorAll('span').length === 0;
};

const injectStatusStylesheet = () => {
  if (globalThis.document.getElementById(INJECTED_STYLE_ID)) return;

  const rules = Object.entries(STATUS_STYLES)
    .map(([key, styles]) => {
      const label = STATUS_LABELS[key as UpdateStatus];

      return `
        html body span[data-status="${key}"] {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          border-radius: 4px;
          font-weight: 500;
          font-size: 0;
          background-color: ${styles.bg};
          border: 1px solid ${styles.border};
        }
        html body span[data-status="${key}"]::after {
          content: "${label}";
          font-size: 13px;
          color: ${styles.color};
        }`;
    })
    .join('\n');

  const style = globalThis.document.createElement('style');
  style.id = INJECTED_STYLE_ID;
  style.textContent = rules;
  globalThis.document.head.appendChild(style);
};

const applyStatusStyles = () => {
  if (!isTargetPage()) return;

  injectStatusStylesheet();

  globalThis.document.querySelectorAll('span').forEach((span) => {
    const text = span.textContent.trim();
    const storedStatus = span.dataset.status;

    if (storedStatus && text !== storedStatus) {
      delete span.dataset.status;
    }

    if (isLeafSpan(span) && STATUS_VALUES.includes(text as UpdateStatus)) {
      span.dataset.status = text as UpdateStatus;
    }
  });
};

// Setup observers and handlers
export const setupStatusStyling = () => {
  const debouncedApplyStyles = debounce(applyStatusStyles, DEBOUNCE_DELAY);

  // Initial application
  setTimeout(applyStatusStyles, INITIAL_STYLE_DELAY);

  // DOM observer
  const observer = new MutationObserver((mutations) => {
    if (!isTargetPage()) return;

    const hasRelevantChanges = mutations.some(
      (mutation) =>
        mutation.type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0),
    );

    if (hasRelevantChanges) {
      debouncedApplyStyles();
    }
  });

  const contentArea = globalThis.document.querySelector('main') || globalThis.document.body;
  observer.observe(contentArea, {
    childList: true,
    subtree: true,
  });

  // Navigation handler
  let currentPath = globalThis.location.pathname;
  const handleNavigation = () => {
    const newPath = globalThis.location.pathname;
    if (newPath !== currentPath) {
      currentPath = newPath;
      globalThis.document.querySelectorAll('span[data-status]').forEach((el) => {
        delete (el as HTMLElement).dataset.status;
      });
      setTimeout(applyStatusStyles, 500);
    }
  };

  globalThis.addEventListener('popstate', handleNavigation);
  const navigationInterval = setInterval(handleNavigation, NAVIGATION_CHECK_INTERVAL);

  // Cleanup
  globalThis.addEventListener('beforeunload', () => {
    observer.disconnect();
    clearInterval(navigationInterval);
    globalThis.removeEventListener('popstate', handleNavigation);
  });
};
